import { useState } from 'react'
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  Bot, 
  User, 
  TrendingUp, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Zap,
  Database,
  Brain,
  Workflow,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Star,
  Users,
  Timer,
  Target,
  ArrowLeft,
  LogOut,
  DollarSign
} from 'lucide-react'
import InfoTooltip from './common/InfoTooltip'

export default function CustomerServiceDemo({ onLogout }) {
  const [activeChannel, setActiveChannel] = useState('whatsapp')
  const [selectedConversation, setSelectedConversation] = useState(0)
  const [showConversationDetail, setShowConversationDetail] = useState(false)
  const [showAnalyticsDetail, setShowAnalyticsDetail] = useState(false)
  const [selectedAnalytic, setSelectedAnalytic] = useState(null)

  // Datos simulados de metricas en tiempo real
  const metrics = [
    {
      title: "Resolución Automática",
      value: "87",
      suffix: "%",
      change: "+12%",
      trend: "up",
      icon: Bot,
      color: "blue",
             tooltip: {
         title: "Resolución Automática Inteligente",
         description: "87% de sus clientes obtienen respuestas instantáneas sin esperar a un agente humano. Esto significa clientes más satisfechos y costos operativos reducidos.",
         aiMethod: "💰 Beneficio: Reducción del 60% en costos de atención al cliente. Sus agentes se enfocan en casos complejos mientras la IA maneja consultas rutinarias 24/7.",
         dataSource: "📈 ROI: Aumento del 35% en satisfacción del cliente y capacidad de atender 5x más consultas con el mismo equipo.",
         updateFrequency: "🚀 Implementación: Sistema listo en 2 semanas, mejora automática con cada interacción"
       }
    },
    {
      title: "Tiempo Promedio",
      value: "2.3",
      suffix: " min",
      change: "-35%",
      trend: "up",
      icon: Clock,
      color: "green",
             tooltip: {
         title: "Respuesta Ultra Rápida",
         description: "Sus clientes reciben soluciones en promedio de 2.3 minutos. Compare esto con 15-30 minutos del servicio tradicional.",
         aiMethod: "💰 Beneficio: Clientes más felices = más ventas. Resolución rápida aumenta la probabilidad de recompra en 40%.",
         dataSource: "📈 ROI: Menos abandonos de carrito, mayor retención de clientes y recomendaciones orgánicas.",
         updateFrequency: "🚀 Implementación: Mejora inmediata desde el día uno, optimización continua"
       }
    },
    {
      title: "Satisfacción",
      value: "4.8",
      suffix: "/5",
      change: "+0.4",
      trend: "up",
      icon: Star,
      color: "yellow",
             tooltip: {
         title: "Clientes Extraordinariamente Satisfechos",
         description: "4.8/5 estrellas de satisfacción. Sus competidores promedian 3.2/5. Esta diferencia se traduce directamente en más ventas.",
         aiMethod: "💰 Beneficio: Clientes satisfechos gastan 67% más y recomiendan 3x más su negocio a otros.",
         dataSource: "📈 ROI: Aumento del 45% en valor de vida del cliente (LTV) y reducción del 70% en quejas escaladas.",
         updateFrequency: "🚀 Implementación: Mejora visible en reseñas online desde la primera semana"
       }
    },
    {
      title: "Escalamientos",
      value: "13",
      suffix: "%",
      change: "-8%",
      trend: "up",
      icon: AlertTriangle,
      color: "orange",
             tooltip: {
         title: "Escalamiento Inteligente",
         description: "Solo 13% de casos necesitan intervención humana. Sus agentes trabajan en casos realmente importantes, no en preguntas repetitivas.",
         aiMethod: "💰 Beneficio: Sus agentes son 4x más productivos enfocándose en ventas y casos complejos en lugar de consultas básicas.",
         dataSource: "📈 ROI: Reducción del 50% en costos de personal mientras mejora la calidad del servicio. Agentes más motivados y eficientes.",
         updateFrequency: "🚀 Implementación: Liberación inmediata de tiempo de sus agentes para actividades de mayor valor"
       }
    }
  ]

  // Datos de canales de comunicación
  const channels = [
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle, color: 'green', active: 234 },
    { id: 'web', name: 'Chat Web', icon: MessageSquare, color: 'blue', active: 156 },
    { id: 'email', name: 'Email', icon: Mail, color: 'red', active: 89 },
    { id: 'phone', name: 'Teléfono', icon: Phone, color: 'purple', active: 45 }
  ]

  // Conversaciones en tiempo real
  const conversations = [
         {
       id: 1,
       channel: 'whatsapp',
       customer: 'María González',
       issue: 'Problema con facturación',
       sentiment: 'frustrated',
       priority: 'high',
       aiConfidence: 0.92,
       status: 'active',
       messages: 8,
       time: '2 min',
       lastMessage: 'No entiendo por qué me cobraron doble...',
       fullConversation: [
         { sender: 'customer', message: 'Hola, tengo un problema con mi factura', time: '14:32', sentiment: 'neutral' },
         { sender: 'ai', message: 'Hola María, soy su asistente inteligente. Con gusto la ayudo con su factura. ¿Podría darme más detalles?', time: '14:32', confidence: 0.95 },
         { sender: 'customer', message: 'Me cobraron doble este mes', time: '14:33', sentiment: 'frustrated' },
         { sender: 'ai', message: 'Entiendo su preocupación. Estoy revisando su cuenta... Veo que hubo un error en el sistema. ¿Puedo procesar un reembolso inmediato?', time: '14:33', confidence: 0.92 },
         { sender: 'customer', message: 'Sí por favor, no entiendo por qué me cobraron doble', time: '14:34', sentiment: 'frustrated' },
         { sender: 'ai', message: 'He procesado el reembolso de $150. Debería verlo en 24-48 horas. También he corregido el error para evitar que vuelva a ocurrir.', time: '14:35', confidence: 0.88 }
       ],
       customerProfile: {
         name: 'María González',
         email: 'maria.gonzalez@email.com',
         phone: '+52 55 1234 5678',
         customerSince: '2022',
         totalOrders: 23,
         avgOrderValue: '$850',
         lastPurchase: '15/01/2024',
         preferredChannel: 'WhatsApp'
       }
     },
         {
       id: 2,
       channel: 'web',
       customer: 'Carlos Mendez',
       issue: 'Consulta de producto',
       sentiment: 'neutral',
       priority: 'medium',
       aiConfidence: 0.98,
       status: 'resolved',
       messages: 4,
       time: '5 min',
       lastMessage: 'Perfecto, gracias por la información',
       fullConversation: [
         { sender: 'customer', message: '¿Tienen disponible el modelo X en color azul?', time: '15:20', sentiment: 'neutral' },
         { sender: 'ai', message: 'Sí, tenemos 15 unidades en stock del modelo X en azul. ¿Le gustaría conocer las especificaciones?', time: '15:20', confidence: 0.98 },
         { sender: 'customer', message: 'Sí, y también el precio', time: '15:21', sentiment: 'positive' },
         { sender: 'ai', message: 'Perfecto, gracias por la información. El modelo X azul cuesta $1,250 con garantía de 2 años. También tengo un descuento del 10% vigente hoy. ¿Le interesa proceder con la compra?', time: '15:21', confidence: 0.96 }
       ],
       customerProfile: {
         name: 'Carlos Mendez',
         email: 'carlos.mendez@email.com',
         phone: '+52 55 9876 5432',
         customerSince: '2023',
         totalOrders: 5,
         avgOrderValue: '$1,200',
         lastPurchase: '08/01/2024',
         preferredChannel: 'Chat Web'
       }
     },
    {
      id: 3,
      channel: 'email',
      customer: 'Ana Ruiz',
      issue: 'Solicitud de reembolso',
      sentiment: 'angry',
      priority: 'high',
      aiConfidence: 0.65,
      status: 'escalated',
      messages: 12,
      time: '15 min',
      lastMessage: 'Exijo hablar con un supervisor inmediatamente'
    }
  ]

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100'
      case 'neutral': return 'text-blue-600 bg-blue-100'
      case 'frustrated': return 'text-yellow-600 bg-yellow-100'
      case 'angry': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-blue-600 bg-blue-100'
      case 'resolved': return 'text-green-600 bg-green-100'
      case 'escalated': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => window.history.back()}
                className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </button>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900">Atención al Cliente IA</h1>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-8">
        {/* Title Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Sistema de Atención al Cliente Inteligente
          </h1>
          <p className="text-gray-600">
            Chatbot multicanal con IA, escalamiento inteligente y análisis de sentimientos en tiempo real
          </p>
        </div>

        {/* Metricas principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm text-gray-600">{metric.title}</p>
                      <InfoTooltip 
                        title={metric.tooltip.title}
                        description={metric.tooltip.description}
                        aiMethod={metric.tooltip.aiMethod}
                        dataSource={metric.tooltip.dataSource}
                        updateFrequency={metric.tooltip.updateFrequency}
                        iconColor="text-gray-400"
                      />
                    </div>
                    <p className="text-2xl font-bold text-gray-900">
                      {metric.value}{metric.suffix}
                    </p>
                    <div className="flex items-center mt-2">
                      <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                        {metric.change}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">vs mes anterior</span>
                    </div>
                  </div>
                  <div className={`p-3 bg-${metric.color}-500 rounded-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Panel de canales */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Canales Activos</h3>
              <InfoTooltip
                title="Sistema Multicanal Unificado"
                description="Plataforma que integra todos los canales de comunicación en una sola interfaz con routing inteligente."
                aiMethod="Algoritmos de routing basado en disponibilidad, especialización de agentes, y prioridad de casos. NLP para clasificación automática."
                dataSource="APIs de WhatsApp Business, sistemas web chat, integración email IMAP/SMTP, telefonía VoIP."
                updateFrequency="Sincronización en tiempo real con webhooks"
                iconColor="text-gray-400"
              />
            </div>
            <div className="space-y-4">
              {channels.map((channel) => {
                const IconComponent = channel.icon
                return (
                  <div 
                    key={channel.id}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      activeChannel === channel.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveChannel(channel.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 bg-${channel.color}-500 rounded-lg`}>
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium text-gray-900">{channel.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">{channel.active}</p>
                        <p className="text-sm text-gray-500">activos</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Panel de conversaciones */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center space-x-2 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Conversaciones en Tiempo Real</h3>
              <InfoTooltip
                title="Monitor de Conversaciones con IA"
                description="Vista en tiempo real de todas las conversaciones activas con análisis automático de sentimientos y priorización inteligente."
                aiMethod="Análisis de sentimientos con VADER y TextBlob, clasificación de urgencia con ML, detección de palabras clave críticas."
                dataSource="Stream de mensajes en tiempo real, historial de interacciones, perfil del cliente, base de conocimiento."
                updateFrequency="Actualización instantánea con WebSockets"
                iconColor="text-gray-400"
              />
            </div>
            <div className="space-y-4">
              {conversations.map((conv, index) => (
                <div 
                  key={conv.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-all ${
                    selectedConversation === index 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedConversation(index)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-medium text-gray-900">{conv.customer}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(conv.sentiment)}`}>
                          {conv.sentiment}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(conv.status)}`}>
                          {conv.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{conv.issue}</p>
                      <p className="text-xs text-gray-500 italic">"{conv.lastMessage}"</p>
                      <div className="flex items-center space-x-4 mt-3 text-xs text-gray-500">
                        <span>{conv.messages} mensajes</span>
                        <span>{conv.time}</span>
                        <span>Confianza IA: {(conv.aiConfidence * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                                         <div className="flex flex-col items-end space-y-2">
                       <div className={`w-3 h-3 rounded-full ${
                         conv.priority === 'high' ? 'bg-red-500' : 
                         conv.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                       }`} />
                       {conv.status === 'escalated' && (
                         <AlertTriangle className="w-4 h-4 text-red-500" />
                       )}
                       <button
                         onClick={(e) => {
                           e.stopPropagation()
                           setSelectedConversation(index)
                           setShowConversationDetail(true)
                         }}
                         className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                       >
                         Ver detalle
                       </button>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

                 {/* Sección de beneficios comerciales */}
         <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
           <div className="flex items-center space-x-2 mb-6">
             <h3 className="text-2xl font-semibold text-gray-900">¿Por qué su Empresa Necesita este Sistema de IA?</h3>
             <Brain className="w-6 h-6 text-purple-600" />
           </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                         {/* Beneficios Inmediatos */}
             <div>
               <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                 <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                 Beneficios Inmediatos para su Negocio
               </h4>
               <div className="space-y-4 text-sm text-gray-700">
                 <div className="p-4 bg-green-50 rounded-lg">
                   <h5 className="font-semibold text-green-900 mb-2">💰 Reducción de Costos Operativos</h5>
                   <p>Ahorre hasta 60% en costos de atención al cliente. El sistema maneja automáticamente consultas rutinarias, liberando a su equipo para enfocarse en ventas y casos complejos que realmente requieren atención humana.</p>
                 </div>
                 <div className="p-4 bg-blue-50 rounded-lg">
                   <h5 className="font-semibold text-blue-900 mb-2">📈 Aumento en Satisfacción del Cliente</h5>
                   <p>Respuestas instantáneas 24/7 resultan en 35% más satisfacción del cliente. Clientes felices compran más, recomiendan más y se quejan menos. Mejora inmediata en reseñas online y reputación.</p>
                 </div>
                 <div className="p-4 bg-purple-50 rounded-lg">
                   <h5 className="font-semibold text-purple-900 mb-2">🚀 Escalabilidad sin Límites</h5>
                   <p>Atienda 5x más clientes con el mismo equipo. No más colas de espera, no más clientes frustrados. El sistema crece con su negocio sin contratar personal adicional.</p>
                 </div>
               </div>
             </div>

                         {/* ROI y Resultados */}
             <div>
               <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                 <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                 Resultados Comprobados y ROI
               </h4>
               <div className="space-y-4 text-sm text-gray-700">
                 <div className="p-4 bg-yellow-50 rounded-lg">
                   <h5 className="font-semibold text-yellow-900 mb-2">⚡ Implementación Rápida</h5>
                   <p>Sistema funcionando en 2 semanas. Sin interrupciones a su operación actual. Capacitación mínima requerida. Su equipo verá resultados desde el primer día.</p>
                 </div>
                 <div className="p-4 bg-red-50 rounded-lg">
                   <h5 className="font-semibold text-red-900 mb-2">📊 ROI Garantizado</h5>
                   <p>Recupere su inversión en 4-6 meses. Casos de éxito muestran ahorros de $50,000-200,000 anuales dependiendo del tamaño de la empresa. Métricas transparentes y medibles.</p>
                 </div>
                 <div className="p-4 bg-indigo-50 rounded-lg">
                   <h5 className="font-semibold text-indigo-900 mb-2">🏆 Ventaja Competitiva</h5>
                   <p>Mientras sus competidores luchan con largas colas y clientes frustrados, usted ofrece servicio instantáneo y personalizado. Capture más market share con mejor experiencia.</p>
                 </div>
               </div>
             </div>
          </div>

                     {/* Metricas de impacto comercial */}
           <div className="mt-8 p-6 bg-gray-50 rounded-lg">
             <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
               <BarChart3 className="w-5 h-5 text-indigo-600 mr-2" />
               Impacto Real en su Negocio
             </h4>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
               <div className="text-center">
                 <div className="text-2xl font-bold text-green-600 mb-1">$85,000</div>
                 <div className="text-gray-600">Ahorro anual promedio en costos operativos</div>
               </div>
               <div className="text-center">
                 <div className="text-2xl font-bold text-blue-600 mb-1">+45%</div>
                 <div className="text-gray-600">Aumento en retención de clientes</div>
               </div>
               <div className="text-center">
                 <div className="text-2xl font-bold text-purple-600 mb-1">6 meses</div>
                 <div className="text-gray-600">Tiempo promedio de recuperación de inversión</div>
               </div>
             </div>
           </div>
        </div>

        {/* Sección de tecnologías */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-6">
            <h3 className="text-2xl font-semibold text-gray-900">Stack Tecnológico</h3>
            <Zap className="w-6 h-6 text-yellow-600" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Backend IA</div>
              <div className="text-sm text-gray-600">
                Python + FastAPI<br/>
                spaCy + Transformers<br/>
                scikit-learn<br/>
                TensorFlow
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Base de Datos</div>
              <div className="text-sm text-gray-600">
                PostgreSQL<br/>
                Redis (cache)<br/>
                Pinecone (vectores)<br/>
                Elasticsearch
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Integraciones</div>
              <div className="text-sm text-gray-600">
                WhatsApp Business API<br/>
                N8N (workflows)<br/>
                Twilio (SMS/Voz)<br/>
                SendGrid (Email)
              </div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-lg font-semibold text-gray-800 mb-2">Monitoreo</div>
              <div className="text-sm text-gray-600">
                Prometheus + Grafana<br/>
                ELK Stack<br/>
                Sentry (errores)<br/>
                WebSockets (real-time)
              </div>
            </div>
          </div>
        </div>

        {/* Modal de detalle de conversación */}
        {showConversationDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
              {/* Header del modal */}
              <div className="bg-blue-600 text-white p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-semibold">Detalle de Conversación</h3>
                    <p className="text-blue-100">{conversations[selectedConversation]?.customer}</p>
                  </div>
                  <button
                    onClick={() => setShowConversationDetail(false)}
                    className="text-white hover:bg-blue-700 rounded-lg p-2"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 h-96 overflow-hidden">
                {/* Chat */}
                <div className="lg:col-span-2 p-4 border-r overflow-y-auto">
                  <h4 className="font-semibold mb-4">Conversación en Tiempo Real</h4>
                  <div className="space-y-3">
                    {conversations[selectedConversation]?.fullConversation?.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          msg.sender === 'customer' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-200 text-gray-800'
                        }`}>
                          <p className="text-sm">{msg.message}</p>
                          <div className="flex justify-between items-center mt-1 text-xs opacity-70">
                            <span>{msg.time}</span>
                            {msg.sender === 'ai' && (
                              <span>IA: {(msg.confidence * 100).toFixed(0)}%</span>
                            )}
                            {msg.sender === 'customer' && msg.sentiment && (
                              <span className={`px-1 rounded ${
                                msg.sentiment === 'positive' ? 'bg-green-500' :
                                msg.sentiment === 'frustrated' ? 'bg-yellow-500' :
                                'bg-gray-500'
                              }`}>
                                {msg.sentiment}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Perfil del cliente */}
                <div className="p-4 bg-gray-50 overflow-y-auto">
                  <h4 className="font-semibold mb-4">Perfil del Cliente</h4>
                  {conversations[selectedConversation]?.customerProfile && (
                    <div className="space-y-3 text-sm">
                      <div>
                        <label className="font-medium text-gray-600">Nombre:</label>
                        <p>{conversations[selectedConversation].customerProfile.name}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Email:</label>
                        <p>{conversations[selectedConversation].customerProfile.email}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Cliente desde:</label>
                        <p>{conversations[selectedConversation].customerProfile.customerSince}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Total de pedidos:</label>
                        <p>{conversations[selectedConversation].customerProfile.totalOrders}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Valor promedio:</label>
                        <p>{conversations[selectedConversation].customerProfile.avgOrderValue}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Última compra:</label>
                        <p>{conversations[selectedConversation].customerProfile.lastPurchase}</p>
                      </div>
                      <div>
                        <label className="font-medium text-gray-600">Canal preferido:</label>
                        <p>{conversations[selectedConversation].customerProfile.preferredChannel}</p>
                      </div>
                    </div>
                  )}

                  {/* Insights de IA */}
                  <div className="mt-6 p-3 bg-blue-50 rounded-lg">
                    <h5 className="font-semibold text-blue-900 mb-2">💡 Insights de IA</h5>
                    <ul className="text-xs text-blue-800 space-y-1">
                      <li>• Cliente de alto valor (top 15%)</li>
                      <li>• Responde bien a ofertas personalizadas</li>
                      <li>• Prefiere resolución rápida</li>
                      <li>• Probabilidad de recompra: 85%</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 border-t">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    💰 <strong>Impacto comercial:</strong> Resolución automática ahorra $15 por caso
                  </div>
                  <button
                    onClick={() => setShowConversationDetail(false)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 